import { Highlight } from 'containers/highlights/types/highlights'
import { AnimatePresence, motion } from 'framer-motion'
import { HighlightThumbnail } from './HighlightThumbnail'
import { HighlightCardContent } from './HighlighCardContent'
import { cssClass } from '@/shared/utils'
import {
  contentInModal,
  imageContainerOnModal,
} from 'containers/highlights/styles/highlight.css'
import { useContext, useMemo, useState } from 'react'
import { BrowserContext } from 'context'
import {
  CloseIcon,
  NewTabIcon,
  ShareArrow,
} from '@/public/assets/v2/images/icons'
import Link from 'next/link'
import { HighlightShareModal } from './HighlightShareModal'
import { Modal } from '@/components/modal/Modal'
import { getLinkSafeUrl } from '@/shared/utils/cleanDomain'
import { useViewPHighlightRequest } from 'containers/highlights/hooks/useViewHighlight'
import { useProfile } from 'containers/profile/hooks/useProfile'

interface HighlightInModalProps {
  highlight: Highlight
  closeModal: () => void
  isOpen: boolean
}

export const HighlightInModal = ({
  highlight,
  closeModal,
  isOpen,
}: HighlightInModalProps) => {
  const { profile } = useProfile()
  const { isDesktop } = useContext(BrowserContext)
  const [shareModalOpen, setShareModalOpen] = useState(false)
  const modalOpen = useMemo(() => {
    if (isDesktop) return isOpen

    setShareModalOpen(false)
    return false
  }, [isDesktop, isOpen])

  useViewPHighlightRequest({
    ownerProfile: profile,
    highlight,
    detailView: true,
  })

  return (
    <>
      <Modal
        className="--v2 full-w p-0"
        onClose={closeModal}
        opened={modalOpen}
        withCloseButton={false}
        classNames={{
          body: 'p-0 rounded-12',
          content: 'rounded-12',
        }}
        size="auto"
        centered
        overlayProps={{
          backgroundOpacity: 0.4,
          blur: 5,
        }}
      >
        {highlight && (
          <AnimatePresence>
            <motion.div layoutId={highlight?.id} className="flex">
              <div className={imageContainerOnModal}>
                <Link
                  href={
                    highlight?.contentLink &&
                    getLinkSafeUrl(highlight?.contentLink)
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <HighlightThumbnail
                    highlight={highlight}
                    alt={highlight?.id + '_in_modal'}
                  />
                </Link>
              </div>
              <div className={cssClass([contentInModal, 'p-24'])}>
                <div className="flex gap-16 justify-flex-end">
                  <ShareArrow
                    className="cursor-pointer text-22"
                    onClick={() => setShareModalOpen(true)}
                  />
                  <Link
                    href={
                      highlight?.contentLink &&
                      getLinkSafeUrl(highlight?.contentLink)
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <NewTabIcon className="cursor-pointer text-22" />
                  </Link>
                  <CloseIcon
                    className="cursor-pointer text-22"
                    onClick={closeModal}
                  />
                </div>
                <HighlightCardContent highlight={highlight} inModal />
              </div>
            </motion.div>
          </AnimatePresence>
        )}
      </Modal>
      <HighlightShareModal
        isOpen={shareModalOpen}
        closeModal={() => setShareModalOpen(false)}
        highlight={highlight}
      />
    </>
  )
}
